import React, { useState, useCallback, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useSearchParams, useNavigate, useParams } from 'react-router-dom';
import { debugLog } from './utils/debug';
import analyticsService from './utils/analytics';
import AudioUploader from './components/AudioUploader';
import AudioPlayer from './components/AudioPlayer';
import Transcript from './components/Transcript';
import ContactUs from './components/ContactUs';

import MobileAudioControls from './components/MobileAudioControls';
import MobileTranscriptControls, { MobileTranscriptControlsSettings } from './components/MobileTranscriptControls';
import TranscriptNameEditDialog from './components/TranscriptNameEditDialog';
import { notifyTranscriptionComplete, requestNotificationPermission } from './utils/notifications';
import { needsConversion, convertToMp3, getFileFormat } from './utils/audioConverter';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { useTierLimits } from './hooks/useTierLimits';
import { ThemeProvider } from './contexts/ThemeContext';
import { useHistory } from './hooks/useHistory';
import { useAutoSave } from './hooks/useAutoSave';
import { useAlert } from './hooks/useAlert';
import { useUndoRedo, useUndoRedoKeyboard } from './hooks/useUndoRedo';
import UserNav from './components/UserNav';
import Auth from './pages/Auth';
import Dashboard from './pages/Dashboard';
import Instructions from './pages/Instructions';
import SharedTranscript from './pages/SharedTranscript';
import TryRecChord from './pages/TryRecChord';
import PaymentSetup from './pages/PaymentSetup';
import Settings from './pages/Settings';
import TranscriptHistory from './pages/TranscriptHistory';
import Billing from './pages/Billing';
import Speakers from './pages/Speakers';
import BulkUpload from './pages/BulkUpload';
import About from './pages/About';
import Admin from './pages/Admin';
import ShareableTranscriptCard from './components/ShareableTranscriptCard';
import DeviceAuth from './components/DeviceAuth';
import CustomAlert from './components/CustomAlert';
import CookieConsent from './components/CookieConsent';
import Layout from './components/Layout';
import PageViewTracker from './components/PageViewTracker';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import './App.css';
import ConnectivityHandler from './components/ConnectivityHandler';
// Use REACT_APP_STRIPE_PUBLISHABLE_KEY (React environment variable)
const getStripeKey = () => {
  return process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
};

const stripeKey = getStripeKey();
const stripePromise = stripeKey ? loadStripe(stripeKey) : null;

if (!stripePromise) {
  console.warn('⚠️ Stripe not initialized - publishable key missing. Payment features will be disabled.');
}

function MainApp() {
  const { user, isAuthenticated, hasActiveSubscription } = useAuth();
  const { checkTranscriptionLimit, promptUpgrade } = useTierLimits();
  const { getTranscript, saveVersion, claimGuestTranscript } = useHistory();
  const { alert, showAlert, showConfirm, showSuccess, showError, showShareLink } = useAlert();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);

  // Undo/Redo state management
  const {
    state: transcriptData,
    setState: setTranscriptDataWithHistory,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory
  } = useUndoRedo(null, 50);

  const [isLoading, setIsLoading] = useState(false);
  const [isAudioLoading, setIsAudioLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState(null);
  const [hasAudio, setHasAudio] = useState(false);
  const [hasTranscript, setHasTranscript] = useState(false);
  const [highThreshold, setHighThreshold] = useState(0.8);
  const [mediumThreshold, setMediumThreshold] = useState(0.2);
  const [typingMode, setTypingMode] = useState(false); // Default typing mode off
  const [autoAdvance, setAutoAdvance] = useState(false);
  const [showConfidence, setShowConfidence] = useState(true); // Show confidence by default for logged in users
  const [showSegments, setShowSegments] = useState(true);
  const [segmentDuration, setSegmentDuration] = useState(10); // Default 10 seconds
  const [clickToDeleteMode, setClickToDeleteMode] = useState(false);
  const [showClickToDeleteToggle, setShowClickToDeleteToggle] = useState(false); // Hidden by default
  const [showSuggestionsModeToggle, setShowSuggestionsModeToggle] = useState(true); // Shown by default
  const [suggestionsMode, setSuggestionsMode] = useState(false); // Suggestions mode off by default
  const [showConfidenceLegend, setShowConfidenceLegend] = useState(true); // Show bin navigation by default
  const [enableBinNavigation, setEnableBinNavigation] = useState(false); // Bin navigation off by default
  const [enableDiarization, setEnableDiarization] = useState(false); // Diarization off by default
  const [originalHash, setOriginalHash] = useState(null);
  const [conversionProgress, setConversionProgress] = useState(0);
  const [isConverting, setIsConverting] = useState(false);
  const [currentTranscriptId, setCurrentTranscriptId] = useState(null);
  const [transcriptName, setTranscriptName] = useState('Untitled Transcript');
  const [isEditingTranscriptName, setIsEditingTranscriptName] = useState(false);
  const [isSettingsVisible, setIsSettingsVisible] = useState(false);
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [skipDuration, setSkipDuration] = useState(5);
  const [showShareCard, setShowShareCard] = useState(false);
  const [shareCardData, setShareCardData] = useState(null);
  const [shareCardTheme, setShareCardTheme] = useState('gradient-blue');
  const [isGuestTranscript, setIsGuestTranscript] = useState(false);
  const [guestExpiresAt, setGuestExpiresAt] = useState(null);
  const [guestMinutesRemaining, setGuestMinutesRemaining] = useState(null);
  const [guestWarningDismissed, setGuestWarningDismissed] = useState(false);
  const audioRef = useRef(null);
  const autoTranscribeTriggered = useRef(false);
  const justCreatedPlaceholder = useRef(false); // Track if we just created a placeholder to avoid loading from server
  const mobileControlsRef = useRef(null);

  // Define isMobile early so it can be used in useEffects
  const isMobile = window.innerWidth <= 768;

  // Debug audioUrl state changes
  useEffect(() => {
    debugLog('[DEBUG] audioUrl state changed:', {
      audioUrl,
      audioUrlType: typeof audioUrl,
      isValidAudioUrl: audioUrl?.startsWith('/api/files/'),
      currentUrl: window.location.href
    });
  }, [audioUrl]);

  // Initialize Google Analytics
  useEffect(() => {
    // Check if user has consented (or initialize if no consent needed for now)
    if (analyticsService.shouldInitialize()) {
      analyticsService.initialize();
    }
  }, []);

  // Listen for share card theme changes
  useEffect(() => {
    const handleThemeChange = (e) => {
      setShareCardTheme(e.detail);
    };
    window.addEventListener('shareCardThemeChange', handleThemeChange);
    return () => window.removeEventListener('shareCardThemeChange', handleThemeChange);
  }, []);

  // Update guest transcript countdown timer
  useEffect(() => {
    if (!guestExpiresAt) {
      setGuestMinutesRemaining(null);
      return;
    }

    const updateCountdown = () => {
      const now = new Date();
      const expiresAt = new Date(guestExpiresAt);
      const minutesLeft = Math.max(0, Math.ceil((expiresAt - now) / (1000 * 60)));
      setGuestMinutesRemaining(minutesLeft);
    };

    // Update immediately
    updateCountdown();

    // Update every 30 seconds
    const interval = setInterval(updateCountdown, 30000);

    return () => clearInterval(interval);
  }, [guestExpiresAt]);

  // Load user preferences
  useEffect(() => {
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    setSkipDuration(preferences.skipDuration || 5);
    setShowConfidence(preferences.showConfidence === true);
    setTypingMode(preferences.typingMode === true);
    setShowSegments(preferences.showSegments !== false); // Default to true (segments shown)
    setSegmentDuration(preferences.segmentDuration || 10); // Default 10 seconds
    setClickToDeleteMode(preferences.clickToDeleteMode === true);
    setShowClickToDeleteToggle(preferences.showClickToDeleteToggle === true); // Default to false (hidden)
    setShowSuggestionsModeToggle(preferences.showSuggestionsModeToggle !== false); // Default to true (shown)
    setShowConfidenceLegend(preferences.showConfidenceLegend !== false); // Default to true (shown)
    setEnableBinNavigation(preferences.enableBinNavigation === true); // Default to false
    setEnableDiarization(preferences.enableDiarization === true); // Default to false
    if (preferences.highThreshold !== undefined) setHighThreshold(preferences.highThreshold);
    if (preferences.mediumThreshold !== undefined) setMediumThreshold(preferences.mediumThreshold);
  }, []);

  // Apply custom highlight colors - separate effect that listens for storage changes
  useEffect(() => {
    const applyCustomColors = () => {
      const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');

      // Apply custom highlight colors to CSS variables ONLY if useCustomColors is enabled
      if (preferences.useCustomColors === true) {
        if (preferences.highConfidenceColor) {
          document.documentElement.style.setProperty('--color-high-confidence', preferences.highConfidenceColor);
        }
        if (preferences.mediumConfidenceColor) {
          document.documentElement.style.setProperty('--color-medium-confidence', preferences.mediumConfidenceColor);
        }
        if (preferences.lowConfidenceColor) {
          document.documentElement.style.setProperty('--color-low-confidence', preferences.lowConfidenceColor);
        }
      } else {
        // Reset to default colors when custom colors are disabled
        document.documentElement.style.removeProperty('--color-high-confidence');
        document.documentElement.style.removeProperty('--color-medium-confidence');
        document.documentElement.style.removeProperty('--color-low-confidence');
      }
    };

    // Apply colors on mount
    applyCustomColors();

    // Listen for storage events from other tabs/windows
    window.addEventListener('storage', applyCustomColors);

    // Listen for custom event from Settings page (same tab)
    window.addEventListener('preferencesChanged', applyCustomColors);

    return () => {
      window.removeEventListener('storage', applyCustomColors);
      window.removeEventListener('preferencesChanged', applyCustomColors);
    };
  }, []);

  // Scroll to top when transcript loads on mobile
  useEffect(() => {
    if (hasTranscript && isMobile) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hasTranscript, isMobile]);

  // Timeout for audio loading to prevent hanging
  useEffect(() => {
    if (isAudioLoading) {
      const timeout = setTimeout(() => {
        console.warn('[DEBUG] Audio loading timeout - forcing loading state to false');
        setIsAudioLoading(false);
      }, 10000); // 10 second timeout

      return () => clearTimeout(timeout);
    }
  }, [isAudioLoading]);

  // Update CSS variable for mobile controls height and body class
  useEffect(() => {
    const updateMobileControlsHeight = () => {
      if (mobileControlsRef.current) {
        // Force a reflow to ensure we get the correct height
        const height = mobileControlsRef.current.getBoundingClientRect().height;
        debugLog('[Mobile Controls] Measured height:', height, 'hasAudio:', hasAudio, 'isMobile:', isMobile);

        if (height > 0) {
          document.documentElement.style.setProperty('--mobile-controls-wrapper-height', `${height}px`);
          document.body.classList.add('mobile-controls-active');
        } else {
          debugLog('[Mobile Controls] Height is 0, using fallback');
        }
      } else {
        debugLog('[Mobile Controls] Ref not available');
        document.body.classList.remove('mobile-controls-active');
        document.documentElement.style.setProperty('--mobile-controls-wrapper-height', '0px');
      }
    };

    if (!hasAudio || !isMobile) {
      // No audio or not mobile, clear the height
      document.body.classList.remove('mobile-controls-active');
      document.documentElement.style.setProperty('--mobile-controls-wrapper-height', '0px');
      return;
    }

    // Multiple measurements to ensure we catch the correct height
    setTimeout(updateMobileControlsHeight, 10);
    setTimeout(updateMobileControlsHeight, 50);
    setTimeout(updateMobileControlsHeight, 100);

    // Update on resize
    window.addEventListener('resize', updateMobileControlsHeight);

    // Update when content changes (ResizeObserver for better performance)
    let resizeObserver;
    if (mobileControlsRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        debugLog('[Mobile Controls] ResizeObserver triggered');
        updateMobileControlsHeight();
      });
      resizeObserver.observe(mobileControlsRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateMobileControlsHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      document.body.classList.remove('mobile-controls-active');
    };
  }, [hasAudio, isMobile, hasTranscript]);

  /**
   * Reset transcript data to original state by removing all user edits
   * This ensures cached transcripts load clean without ghosted words or insertions
   */
  const resetTranscriptToOriginal = useCallback((transcriptData) => {
    if (!transcriptData) return transcriptData;

    // Deep clone to avoid mutating the original
    const cleanData = JSON.parse(JSON.stringify(transcriptData));

    // Reset segments
    if (cleanData.segments && Array.isArray(cleanData.segments)) {
      cleanData.segments = cleanData.segments.map(segment => ({
        ...segment,
        words: segment.words ? segment.words
          // Remove ALL inserted words (these are edits/replacements)
          .filter(word => !word.inserted && !word.isInserted)
          .map(word => {
            return {
              ...word,
              // Remove ALL ghost states (un-delete and un-ghost all words)
              ghost: false,
              isGhosted: false,
              // Remove insertion flags
              inserted: false,
              isInserted: false
            };
          }) : []
      }));
    }

    console.log('[resetTranscriptToOriginal] Reset transcript to original state - removed all edits');
    return cleanData;
  }, []);

  // Debug loading overlay states
  useEffect(() => {
    debugLog('[DEBUG] Loading overlay states:', {
      isLoading,
      isConverting,
      isAudioLoading,
      hasTranscriptParam: !!searchParams.get('transcript')
    });
  }, [isLoading, isConverting, isAudioLoading, searchParams]);

  // Debug AudioPlayer props - moved after canShowVisualization is defined

  // Clear transcription loading when audio loading completes
  // This effect should not run - we now clear isLoading immediately in loadTranscriptById
  // Keeping for safety but shouldn't be needed
  useEffect(() => {
    if (!isAudioLoading && hasAudio && isLoading && hasTranscript && !justCreatedPlaceholder.current) {
      debugLog('[DEBUG] Audio loading complete (fallback), clearing transcription loading state');
      setIsLoading(false);
    }
  }, [isAudioLoading, hasAudio, isLoading, hasTranscript]);

  // Ensure audio element loads on mobile when URL changes
  useEffect(() => {
    if (isMobile && audioRef?.current && audioUrl && hasAudio) {
      console.log('[Mobile Audio] audioUrl changed, ensuring load:', {
        audioUrl,
        readyState: audioRef.current.readyState,
        networkState: audioRef.current.networkState
      });
      // Force load if not already loading/loaded
      if (audioRef.current.readyState === 0) {
        console.log('[Mobile Audio] Calling load() to initialize audio');
        audioRef.current.load();
      }
    }
  }, [isMobile, audioUrl, hasAudio]);

  // Removed: Duplicate auto-transcribe - now handled by the general auto-transcribe effect below (line ~690)

  // Autosave integration
  const getTranscriptData = useCallback(() => {
    return transcriptData;
  }, [transcriptData]);

  const autoSave = useAutoSave(
    currentTranscriptId,
    getTranscriptData,
    30000 // 30 second intervals
  );

  // Request notification permission on component mount
  useEffect(() => {
    requestNotificationPermission();
  }, []);

  // Load transcript if transcript ID is provided in URL, clear if not
  useEffect(() => {
    const transcriptId = searchParams.get('transcript');

    debugLog('[App] useEffect transcript load:', {
      transcriptId,
      transcriptIdLength: transcriptId?.length,
      lastChar: transcriptId?.charAt(transcriptId?.length - 1),
      lastCharCode: transcriptId?.charCodeAt(transcriptId?.length - 1),
      isAuthenticated,
      willLoad: !!(transcriptId && isAuthenticated)
    });

    if (transcriptId && isAuthenticated && !isLoading && !justCreatedPlaceholder.current) {
      // Only load transcript from server if we're not currently transcribing
      // and we didn't just create a placeholder
      debugLog('[App] Calling loadTranscriptById for:', transcriptId);
      loadTranscriptById(transcriptId);
    } else if (transcriptId && isAuthenticated && justCreatedPlaceholder.current) {
      debugLog('[App] Skipping loadTranscriptById - just created placeholder');
    } else if (transcriptId && !isAuthenticated) {
      // For guest users with a transcript ID:
      // - If we have local audio/transcript data, allow viewing (guest just recorded)
      // - If we're loading, allow (transcription in progress)
      // - Otherwise try to load from server (guest transcript within 2 hours)
      if (hasAudio || hasTranscript || isLoading || justCreatedPlaceholder.current) {
        debugLog('[App] Guest user viewing their own transcript - allowing');
        // Guest can view their own transcript from current session
      } else {
        debugLog('[App] Guest user with transcript ID - attempting to load from server');
        // Try to load the transcript - backend allows guest access within 2 hours
        loadTranscriptById(transcriptId);
      }
    } else if (!transcriptId && !justCreatedPlaceholder.current) {
      // Clear transcript/error when navigating to home without transcript ID
      // BUT DON'T CLEAR if we just created a placeholder (in the middle of upload flow)

      // Always clear error when navigating to home (regardless of loading state)
      if (error) {
        debugLog('[DEBUG] Clearing error - navigated to home');
        setError(null);
      }

      // Clear transcript state if we have any transcript/audio data
      // Only clear if NOT currently loading (to avoid interrupting active transcription)
      if (!isLoading && (hasTranscript || hasAudio || currentTranscriptId)) {
        debugLog('[DEBUG] Clearing transcript state - navigated to home');
        clearHistory(null);
        setHasTranscript(false);
        setAudioUrl(null);
        setAudioFile(null);
        setHasAudio(false);
        setCurrentTranscriptId(null);
        setOriginalHash(null);
        setTranscriptName('');
        setCurrentTime(0);
        setDuration(0);
        setIsLoading(false);
        setIsAudioLoading(false);
        autoTranscribeTriggered.current = false;
      }
    }
  }, [searchParams, isAuthenticated, hasTranscript, hasAudio, currentTranscriptId, navigate, isLoading]);

  const loadTranscriptById = async (transcriptId) => {
    try {
      debugLog('[DEBUG] Loading transcript by ID:', transcriptId);
      // Don't set isLoading yet - only set if we need to wait for transcription
      setError(null);

      const transcript = await getTranscript(transcriptId);
      debugLog('[DEBUG] Transcript loaded:', {
        transcript,
        hasData: !!transcript?.data,
        audioHash: transcript?.audioHash,
        transcriptKeys: transcript ? Object.keys(transcript) : 'null'
      });

      // Show detailed debugging
      if (!transcript) {
        console.error('ERROR: getTranscript returned null/undefined');
        setError('Transcript not found');
        return;
      }

      // Set basic transcript info regardless of whether data exists
      setOriginalHash(transcript.audioHash);
      setCurrentTranscriptId(transcriptId); // Enable autosave for this transcript
      setTranscriptName(transcript.name || 'Untitled Transcript');

      // Track guest transcript status for warnings
      setIsGuestTranscript(transcript.isGuestTranscript || false);
      setGuestExpiresAt(transcript.guestExpiresAt || null);
      // Reset warning dismissed state when loading a new transcript
      if (transcript.isGuestTranscript) {
        setGuestWarningDismissed(false);
      }

      // Load transcript-specific settings (with fallback to user defaults from localStorage)
      if (transcript.settings && typeof transcript.settings === 'object') {
        const userPrefs = JSON.parse(localStorage.getItem('userPreferences') || '{}');

        // Apply transcript settings, falling back to user preferences
        if (transcript.settings.segmentDuration !== undefined) {
          setSegmentDuration(transcript.settings.segmentDuration);
        } else if (userPrefs.segmentDuration !== undefined) {
          setSegmentDuration(userPrefs.segmentDuration);
        }

        if (transcript.settings.showConfidence !== undefined) {
          setShowConfidence(transcript.settings.showConfidence);
        } else if (userPrefs.showConfidence !== undefined) {
          setShowConfidence(userPrefs.showConfidence === true);
        }

        if (transcript.settings.showSegments !== undefined) {
          setShowSegments(transcript.settings.showSegments);
        } else if (userPrefs.showSegments !== undefined) {
          setShowSegments(userPrefs.showSegments !== false);
        }

        if (transcript.settings.showConfidenceLegend !== undefined) {
          setShowConfidenceLegend(transcript.settings.showConfidenceLegend);
        } else if (userPrefs.showConfidenceLegend !== undefined) {
          setShowConfidenceLegend(userPrefs.showConfidenceLegend !== false);
        }

        if (transcript.settings.enableBinNavigation !== undefined) {
          setEnableBinNavigation(transcript.settings.enableBinNavigation);
        } else if (userPrefs.enableBinNavigation !== undefined) {
          setEnableBinNavigation(userPrefs.enableBinNavigation === true);
        }

        if (transcript.settings.showSuggestionsModeToggle !== undefined) {
          setShowSuggestionsModeToggle(transcript.settings.showSuggestionsModeToggle);
        } else if (userPrefs.showSuggestionsModeToggle !== undefined) {
          setShowSuggestionsModeToggle(userPrefs.showSuggestionsModeToggle !== false);
        }

        if (transcript.settings.enableDiarization !== undefined) {
          setEnableDiarization(transcript.settings.enableDiarization);
        } else if (userPrefs.enableDiarization !== undefined) {
          setEnableDiarization(userPrefs.enableDiarization === true);
        }
      }

      if (!transcript.data) {
        console.warn(`Transcript has no data (transcription may have failed). Keys: ${Object.keys(transcript).join(', ')}`);
        // Set empty transcript data but continue to load audio
        clearHistory(null);
        setHasTranscript(false);

        // Check if there's a pending job for this transcript
        if (transcript.pendingJobId) {
          console.log(`[loadTranscriptById] Found pending job ${transcript.pendingJobId} for transcript, starting polling...`);
          setIsLoading(true); // Only set loading if there's a pending job
          justCreatedPlaceholder.current = true; // Mark as having pending job

          // Start polling for job completion
          const pollInterval = setInterval(async () => {
            try {
              const token = localStorage.getItem('authToken');
              const jobResponse = await fetch(`/api/jobs/${transcript.pendingJobId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });

              if (!jobResponse.ok) {
                clearInterval(pollInterval);
                setIsLoading(false);
                justCreatedPlaceholder.current = false;
                return;
              }

              const jobStatus = await jobResponse.json();

              if (jobStatus.status === 'completed') {
                clearInterval(pollInterval);
                justCreatedPlaceholder.current = false;
                // Reload the transcript to get the completed data
                await loadTranscriptById(transcriptId);
              } else if (jobStatus.status === 'failed') {
                clearInterval(pollInterval);
                setIsLoading(false);
                justCreatedPlaceholder.current = false;
                setError(jobStatus.error || 'Transcription failed');
              }
            } catch (error) {
              console.error('Error polling job status:', error);
              clearInterval(pollInterval);
              setIsLoading(false);
              justCreatedPlaceholder.current = false;
            }
          }, 2000);
        }
        // Don't return - continue to load audio below
      } else {
        debugLog('[DEBUG] Setting transcript data, data type:', typeof transcript.data);
        debugLog(`Loading transcript: data type=${typeof transcript.data}, hasSegments=${!!transcript.data?.segments}`);

        // Reset transcript to original state (remove ghost/inserted flags)
        const cleanData = resetTranscriptToOriginal(transcript.data);

        // Loading from server - bypass history with clean data
        clearHistory(cleanData);
        setHasTranscript(true);
        // Clear loading state if we have complete data and no pending job
        if (!justCreatedPlaceholder.current) {
          setIsLoading(false);
        }
      }

      // Try to load associated audio file from hash
      if (transcript.audioHash) {
        // Skip fetching if we already have audio from upload (blob URL)
        if (audioUrl && audioFile) {
          debugLog('[DEBUG] Skipping audio fetch - already have blob URL from upload');
          // Don't clear loading if we're in the middle of transcription
          if (!justCreatedPlaceholder.current) {
            setIsLoading(false);
          }
          return;
        }

        try {
          // Fetch audio URL (may return signed URL for GCS or direct stream for local)
          const audioApiUrl = `/api/files/${transcript.audioHash}/audio`;
          debugLog('[DEBUG] Fetching audio URL for transcript:', {
            audioApiUrl,
            audioHash: transcript.audioHash,
            transcriptId: transcriptId
          });

          const audioResponse = await fetch(audioApiUrl, {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('authToken')}`
            }
          });

          if (!audioResponse.ok) {
            const errorMsg = audioResponse.status === 403
              ? 'Access denied: You do not have permission to access this audio file'
              : 'Failed to fetch audio URL';
            throw new Error(errorMsg);
          }

          // Check if response is JSON (signed URL) or direct stream
          const contentType = audioResponse.headers.get('content-type');
          let audioUrl;

          if (contentType && contentType.includes('application/json')) {
            // GCS signed URL
            const data = await audioResponse.json();
            audioUrl = data.url || data.signedUrl;
            debugLog('[DEBUG] Using signed URL for audio:', audioUrl);
          } else {
            // Local file - use the API endpoint directly
            audioUrl = audioApiUrl;
            debugLog('[DEBUG] Using direct stream for audio:', audioUrl);
          }

          setAudioUrl(audioUrl);
          setHasAudio(true);
          // Reset audio loading state - AudioPlayer will set it to true if needed via onLoadingChange
          setIsAudioLoading(false);
          // Reset auto-transcribe flag for loaded transcript
          autoTranscribeTriggered.current = false;
          debugLog('[DEBUG] Audio URL set for transcript:', audioUrl);
          // Clear transcript loading - audio has its own loading state in AudioPlayer
          // Don't clear loading if we're in the middle of transcription
          if (!justCreatedPlaceholder.current) {
            setIsLoading(false);
          }
        } catch (audioError) {
          console.error('Failed to set audio URL:', audioError);
          setHasAudio(false);
          setError(audioError.message);
          // Don't clear loading if we're in the middle of transcription
          if (!justCreatedPlaceholder.current) {
            setIsLoading(false);
          }
        }
      } else {
        // No audio associated with transcript
        console.warn('[loadTranscriptById] No audio hash for transcript');
        // Show error since audio is missing
        const errorMsg = transcript.data
          ? 'This transcript has no audio file. The audio may have been deleted or the transcript was created without uploading audio.'
          : 'This transcript has no audio or transcription data. It may have been created incorrectly or the files may be missing.';
        setError(errorMsg);
        // Don't clear loading if we're in the middle of transcription
        if (!justCreatedPlaceholder.current) {
          setIsLoading(false);
        }
      }
    } catch (err) {
      console.error('Error loading transcript:', err);

      // Check for specific HTTP status codes in error message
      let errorMessage;
      if (err.message.includes('403')) {
        errorMessage = 'Access denied. This transcript does not exist or you do not have permission to view it.';
      } else if (err.message.includes('404')) {
        errorMessage = 'Transcript not found. It may have been deleted or you may not have permission to view it.';
      } else if (err.message.includes('Authentication required') || err.message.includes('401') || err.message.includes('expired')) {
        // Redirect to auth for authentication errors (including expired guest transcripts)
        navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname + window.location.search)}`, { replace: true });
        return;
      } else {
        errorMessage = `Failed to load transcript: ${err.message}`;
      }

      setError(errorMessage);
      setIsLoading(false);
    }
  };

      const handleFileUpload = useCallback(async (file, fileType, recordingTimestamp = null) => {
    console.log('[App] handleFileUpload called with timestamp:', recordingTimestamp);
    setError(null);
    setIsConverting(false);
    setConversionProgress(0);

    const isAudioFile = file?.type?.startsWith('audio/') || false;
    const isJsonFile = file?.type === 'application/json' || file?.name?.toLowerCase().endsWith('.json') || false;

    if (fileType === 'audio' || isAudioFile) {
      if (!isAudioFile) {
        setError('Invalid file type. Please upload an audio file.');
        return;
      }

      // Handle conversion FIRST if needed, THEN show UI
      let processedFile = file;

      // Check if file needs conversion to MP3
      if (needsConversion(file)) {
        try {
          setIsConverting(true);
          const fileFormat = getFileFormat(file);
          debugLog(`Converting ${fileFormat} file to MP3...`);

          processedFile = await convertToMp3(file, (progress) => {
            setConversionProgress(progress);
          });

          debugLog('Audio conversion completed successfully');
          
        } catch (conversionError) {
          console.error('Audio conversion failed:', conversionError);
          setError(`Failed to convert ${getFileFormat(file)} file to MP3. ${conversionError.message}`);
          setIsConverting(false);
          return;
        } finally {
          setIsConverting(false);
          setConversionProgress(0);
        }
      }

      // NOW show UI with the final processed file
      const newAudioUrl = URL.createObjectURL(processedFile);
      debugLog('[DEBUG] handleFileUpload setting audio URL:', {
        newAudioUrl,
        fileName: processedFile.name,
        fileType: processedFile.type
      });
      setAudioUrl(newAudioUrl);
      setAudioFile(processedFile);
      setHasAudio(true);
      clearHistory(null);
      setHasTranscript(false);
      setDuration(0);
      setCurrentTime(0);

      // Set transcript name from audio file name or timestamp
      if (recordingTimestamp) {
        const date = new Date(recordingTimestamp);
        console.log('[Timestamp] Recording started at:', {
          timestamp: recordingTimestamp,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          localTime: date.toLocaleString(),
          isoTime: date.toISOString()
        });
        const formattedName = date.toLocaleString(undefined, {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        console.log('[Timestamp] Transcript name set to:', formattedName);
        setTranscriptName(formattedName);
      } else {
        const fileNameWithoutExt = processedFile.name.replace(/\.[^/.]+$/, '');
        setTranscriptName(fileNameWithoutExt || 'Untitled Transcript');
      }

      // Reset auto-transcribe flag for new file
      autoTranscribeTriggered.current = false;

    } else if (fileType === 'json' || isJsonFile) {
      if (!isJsonFile) {
        setError('Invalid file type. Please upload a JSON file.');
        setIsLoading(false);
        return;
      }
      
      try {
        const text = await file.text();
        const data = JSON.parse(text);

        if (data.segments && data.segments.some(s => s.words && s.words.some(w => w.status))) {
          for (const segment of data.segments) {
            if (!segment.words) continue;
            for (const word of segment.words) {
              switch (word.status) {
                case 'deleted':
                  word.isGhosted = true;
                  break;
                case 'inserted':
                  word.isInserted = true;
                  break;
                case 'original':
                default:
                  break;
              }
              delete word.status;
            }
          }
        }

        clearHistory(data);
        setHasTranscript(true);
        if (data.metadata?.hash) {
          setOriginalHash(data.metadata.hash);
        } else if (data.correctionMetadata?.originalHash) {
          setOriginalHash(data.correctionMetadata.originalHash);
        }
      } catch (parseError) {
        setError('Invalid JSON format. Please upload a valid JSON file.');
      } finally {
        setIsLoading(false);
      }
    } else {
      setError('Unsupported file type. Please upload an audio or JSON file.');
      setIsLoading(false);
    }
  }, [clearHistory]);

  // Live transcription handlers
  const handleLiveTranscriptUpdate = useCallback((newSegments) => {
    console.log('[LiveTranscription] Received new segments:', newSegments.length);

    // Append new segments to existing transcript data
    setTranscriptDataWithHistory(prevData => {
      const segments = prevData?.segments || [];
      return {
        ...prevData,
        segments: [...segments, ...newSegments],
        text: [...segments, ...newSegments].map(s => s.text).join(' '),
        language: prevData?.language || 'en'
      };
    });

    // Set hasTranscript to show the UI
    if (!hasTranscript) {
      setHasTranscript(true);
    }
  }, [setTranscriptDataWithHistory, hasTranscript]);

  const handleLiveSessionComplete = useCallback(async (transcript, allSegments) => {
    console.log('[LiveTranscription] Session completed:', {
      transcriptId: transcript?.id,
      transcriptName: transcript?.name,
      segmentCount: allSegments?.length,
      transcript: transcript
    });

    if (!transcript || !transcript.id) {
      console.error('[LiveSessionComplete] No transcript ID provided, received:', transcript);
      setError('Failed to save live transcription - no transcript ID received from server');
      return;
    }

    // Navigate to the transcript URL - this will trigger loadTranscriptById via useEffect
    console.log('[LiveSessionComplete] Navigating to transcript:', transcript.id);
    navigate(`/?transcript=${transcript.id}`, { replace: true });

    // Show success message
    showSuccess('Live Transcription Complete', `Your transcript "${transcript.name}" has been saved!`);
  }, [navigate, showSuccess, setError]);

  const handleTranscribe = useCallback(async () => {
    debugLog('[handleTranscribe] Called with state:', {
      isLoading,
      hasTranscript,
      hasTranscriptData: !!transcriptData,
      hasAudioFile: !!audioFile,
      isAuthenticated,
      userPaymentStatus: user?.paymentStatus
    });

    // Prevent duplicate transcription requests
    if (isLoading) {
      debugLog('[handleTranscribe] BLOCKED: Transcription already in progress');
      return;
    }

    // Check if we already have a transcript for this audio
    if (hasTranscript && transcriptData) {
      debugLog('[handleTranscribe] BLOCKED: Transcript already exists');
      return;
    }

    debugLog('[handleTranscribe] Setting isLoading to true, starting transcription...');
    setIsLoading(true);
    setError(null);

    if (!audioFile) {
      debugLog('[handleTranscribe] BLOCKED: No audio file');
      setError('No audio file available to transcribe.');
      setIsLoading(false);
      return;
    }

    // Transcription is free for trial - login required only for save/export
    // Skip tier limits for unauthenticated users (they get one free trial)
    if (!isAuthenticated) {
      debugLog('[handleTranscribe] Allowing free trial transcription for unauthenticated user');
    }

    // Check tier limits (only for authenticated users)
    if (isAuthenticated) {
      // TODO: Get actual usage count from API
      const currentUsage = 0; // This should come from user's monthly usage
      const limitCheck = checkTranscriptionLimit(currentUsage);

      if (!limitCheck.allowed) {
        debugLog('[handleTranscribe] BLOCKED: Tier limit reached');
        const upgraded = promptUpgrade(limitCheck.message, limitCheck.upgradeMessage);
        if (!upgraded) {
          setIsLoading(false);
          return; // User cancelled upgrade
        }
      }
    }

    const formData = new FormData();
    formData.append('audio', audioFile);

    // Include transcript name if we have one (from recording timestamp or file name)
    if (transcriptName && transcriptName !== 'Untitled Transcript') {
      formData.append('transcriptName', transcriptName);
    }
    // Add diarization preference FIRST
    // Add diarization preference if enabled (use current state which may be from transcript settings)
    if (enableDiarization === true) {
      formData.append("enableDiarization", "true");
    }

    // Get auth token from localStorage
    const token = localStorage.getItem("authToken");
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // Debug: Check for auth state mismatch
    console.log('[handleTranscribe] Auth debug:', {
      isAuthenticated,
      hasToken: !!token,
      tokenPreview: token ? token.substring(0, 20) + '...' : 'null',
      userEmail: user?.email || 'none'
    });

    if (isAuthenticated && !token) {
      console.error('[handleTranscribe] WARNING: User appears authenticated but no token in localStorage!');
    }

    // Only block users with failed or cancelled payments
    if (user && (user.paymentStatus === 'failed' || user.paymentStatus === 'cancelled')) {
      debugLog('[handleTranscribe] BLOCKED: Payment status', user.paymentStatus);
      setError('Payment required for transcription services. Please update your payment method.');
      setIsLoading(false);
      return;
    }

    debugLog('[handleTranscribe] All checks passed, sending async transcription request...');

    // Function to download the audio file
    const downloadAudioFile = () => {
      if (audioFile) {
        const url = URL.createObjectURL(audioFile);
        const a = document.createElement('a');
        a.href = url;
        const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
        a.download = audioFile.name || `recording-${timestamp}.${audioFile.type.split('/')[1] || 'webm'}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        showSuccess('Download Started', 'Your audio file is being downloaded. You can upload it again when your connection improves.');
      }
    };

    try {
      // Use same endpoint for both authenticated and guest users
      // Server uses guest account when no token provided
      const response = await fetch('/transcribe-async', {
        method: 'POST',
        headers,
        body: formData,
      });

      if (!response.ok) {
        let errData = null;
        try {
          errData = await response.json();
        } catch (jsonError) {
          // JSON parsing failed, errData remains null
        }

        // If guest limit reached, redirect to login
        if (errData?.requiresLogin) {
          navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
          setIsLoading(false);
          return;
        }

        // Create error object that includes status and response data
        const error = new Error(errData?.error || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = errData;
        throw error;
      }

      const responseData = await response.json();

      // Both authenticated and guest users go through job polling now
      // Guest transcripts are saved under the guest account
      const jobId = responseData.jobId;
      const transcriptId = responseData.transcriptId;
      const isGuestSession = responseData.isGuest || false;

      debugLog('[handleTranscribe] isGuest:', isGuestSession);

      debugLog('[handleTranscribe] Job created:', jobId, 'transcriptId:', transcriptId, '- polling for completion...');

      // Navigate to transcript URL immediately so user can see audio player
      // Set hasTranscript to true so the UI shows the transcript section
      if (transcriptId) {
        setCurrentTranscriptId(transcriptId);

        // Set guest transcript state for new transcripts
        setIsGuestTranscript(isGuestSession);
        if (isGuestSession) {
          // Set expiry to 2 hours from now for new guest transcripts
          const expiresAt = new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString();
          setGuestExpiresAt(expiresAt);
          setGuestWarningDismissed(false);
        } else {
          setGuestExpiresAt(null);
        }

        // Check if AI naming is enabled - if so, show loading message instead of filename
        const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
        if (preferences.useAiGeneratedNames) {
          setTranscriptName('Generating name...');
        } else {
          setTranscriptName(transcriptName || 'Untitled Transcript');  // Set the name
        }

        setHasTranscript(true);  // Show transcript UI immediately
        clearHistory({ segments: [], text: '', language: '' });  // Empty placeholder - bypass history
        setIsLoading(true);  // Show loading state for transcription
        justCreatedPlaceholder.current = true;  // Prevent loading from server

        // Create local object URL from uploaded file for immediate playback
        if (audioFile) {
          const localAudioUrl = URL.createObjectURL(audioFile);
          setAudioUrl(localAudioUrl);
          setHasAudio(true);
          console.log('[handleTranscribe] Set local audio URL from uploaded file');
        }

        navigate(`/?transcript=${transcriptId}`, { replace: true });
        console.log('[handleTranscribe] Navigated to transcript URL:', transcriptId);
      }

      // Poll for job completion AND check transcript for audio hash updates
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/jobs/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!statusResponse.ok) {
            clearInterval(pollInterval);
            setIsLoading(false);
            throw new Error('Failed to check job status');
          }

          const jobStatus = await statusResponse.json();
          debugLog('[handleTranscribe] Job status:', jobStatus.status, `(${jobStatus.progress}%)`);

          // While job is processing, poll transcript to check for audio hash
          if (jobStatus.status === 'processing' && transcriptId && !hasAudio) {
            try {
              const transcriptResponse = await fetch(`/api/transcripts/${transcriptId}`, {
                headers: { 'Authorization': `Bearer ${token}` }
              });
              if (transcriptResponse.ok) {
                const transcriptData = await transcriptResponse.json();
                // If audio hash is set, load the audio
                if (transcriptData.audioHash && !audioUrl) {
                  debugLog('[handleTranscribe] Audio hash detected, loading audio...');
                  await loadTranscriptById(transcriptId);
                }
              }
            } catch (err) {
              console.error('[handleTranscribe] Error checking transcript for audio:', err);
            }
          }

          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);

            // Get job result data for analytics and metadata
            const data = jobStatus.result;

            // Only reload transcript if user is still on the home page viewing this transcript
            // Don't navigate users away from dashboard/settings when job completes
            const currentPath = window.location.pathname;
            const isOnHomePage = currentPath === '/' || currentPath === '';

            // Load the full transcript from server to get audio URL and all metadata
            // This will set transcript data, audio URL, and handle alternatives
            if (transcriptId && isOnHomePage) {
              await loadTranscriptById(transcriptId);
              // Don't overwrite transcript data with jobStatus.result
              // loadTranscriptById has already set the latest data from server
            } else if (!isOnHomePage) {
              console.log(`[Job Polling] Job ${jobId} completed but user is on ${currentPath}, not reloading transcript`);
            } else {
              // Fallback if no transcriptId (shouldn't happen) - bypass history
              clearHistory(data);
              setHasTranscript(true);
            }

            // Clear loading state and reset flag AFTER reloading transcript
            setIsLoading(false);
            justCreatedPlaceholder.current = false;  // Reset flag - transcription complete

            // Check if user has enabled AI-generated names
            const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
            console.log('[AI Name] Preferences:', { useAiGeneratedNames: preferences.useAiGeneratedNames, transcriptId });

            // Generate AI name if preference is enabled, regardless of current name
            if (preferences.useAiGeneratedNames && transcriptId) {
                console.log('[AI Name] Attempting to generate AI name for transcript:', transcriptId);
                // Use a temporary name while AI generates
                setTranscriptName('Generating name...');

                // Generate AI name in background (don't block UI)
                fetch(`/api/transcripts/${transcriptId}/generate-name`, {
                  method: 'POST',
                  headers: {
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                    'Content-Type': 'application/json'
                  }
                })
                  .then(res => {
                    console.log('[AI Name] Response status:', res.status);
                    if (!res.ok) {
                      throw new Error(`HTTP ${res.status}: ${res.statusText}`);
                    }
                    return res.json();
                  })
                  .then(result => {
                    console.log('[AI Name] Response data:', result);
                    if (result.name) {
                      console.log('[AI Name] AI-generated name received:', result.name);
                      setTranscriptName(result.name);
                    } else {
                      throw new Error('No name in response');
                    }
                  })
                  .catch(err => {
                    console.error('[AI Name] Failed to generate AI name:', err);
                    // Fallback to datetime name
                    const now = new Date();
                    const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                    const fallbackName = `Transcript ${dateStr} ${timeStr}`;
                    console.log('[AI Name] Using fallback name:', fallbackName);
                    setTranscriptName(fallbackName);
                  });
            } else if (!transcriptName || transcriptName === 'Untitled Transcript') {
              // Only set a default datetime name if no name exists and AI names are disabled
              const now = new Date();
              const dateStr = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
              const defaultName = `Transcript ${dateStr} ${timeStr}`;
              console.log('[AI Name] useAiGeneratedNames is disabled, setting default name:', defaultName);
              setTranscriptName(defaultName);
            } else {
              console.log('[AI Name] Preserving existing name:', transcriptName);
            }

            // Store the hash from the transcript metadata if available
            if (data?.metadata?.hash) {
              setOriginalHash(data.metadata.hash);
            }

            // Track successful transcription
            analyticsService.trackTranscription({
              method: 'whisper',
              fileSize: audioFile?.size || 0,
              duration: data?.segments?.reduce((acc, seg) => Math.max(acc, seg.end || 0), 0) || 0,
              subscriptionTier: user?.tier || 'free',
              success: true
            });

            // transcriptId is already set from the job response, no need to set again
            // The URL was already updated when the job was created

            // Play success notification
            await notifyTranscriptionComplete(true, 'Audio transcription completed successfully!');

          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setIsLoading(false);
            const errorMsg = jobStatus.error || 'Transcription failed';
            setError(errorMsg);
            setHasTranscript(false);
            await notifyTranscriptionComplete(false, errorMsg);
          }
        } catch (pollErr) {
          console.error('[handleTranscribe] Polling error:', pollErr);

          // Check if it's a network error during polling
          const isNetworkError = pollErr instanceof TypeError && pollErr.message.includes('fetch') ||
                               pollErr.message.includes('Network') ||
                               pollErr.message.includes('Failed to fetch') ||
                               pollErr.message.includes('NetworkError') ||
                               pollErr.message.includes('net::ERR');

          if (isNetworkError) {
            // Don't stop polling immediately on network errors - retry a few times
            console.log('[handleTranscribe] Network error during polling, will retry...');
            // Continue polling - the interval will retry
          } else {
            // Other error - stop polling
            clearInterval(pollInterval);
            setIsLoading(false);
            setError(pollErr.message);
            await notifyTranscriptionComplete(false, pollErr.message);
          }
        }
      }, 2000); // Poll every 2 seconds
    } catch (err) {
      console.error('Transcription request error:', err);
      setHasTranscript(false);
      setIsLoading(false);

      // Check if it's a subscription limit error (402 Payment Required)
      if (err.status === 402) {
        const message = err.data?.message || 'You have reached your transcription limit.';
        const upgradePrompt = err.data?.upgradePrompt || 'Please upgrade your plan to continue transcribing.';

        // Show upgrade prompt to user
        const upgrade = await showConfirm(
          'Transcription Limit Reached',
          `${message}\n\n${upgradePrompt}`,
          {
            confirmText: 'Upgrade Now',
            cancelText: 'Cancel'
          }
        );

        if (upgrade) {
          // Navigate to pricing/upgrade page
          navigate('/settings?tab=subscription');
        }
        setError(message);
        await notifyTranscriptionComplete(false, 'Limit reached');
        return;
      }

      // Check if it's a network error
      const isNetworkError = err instanceof TypeError && err.message.includes('fetch') ||
                           err.message.includes('Network') ||
                           err.message.includes('Failed to fetch') ||
                           err.message.includes('NetworkError') ||
                           err.message.includes('net::ERR');

      if (isNetworkError) {
        // Network error - offer download and retry
        const errorMessage = 'Upload failed due to poor internet connection. Your audio is safe and can be downloaded.';
        setError(errorMessage);

        // Show dialog with download and retry options
        const retry = await showConfirm(
          'Upload Failed - Network Error',
          `${errorMessage}\n\nWould you like to:\n\n• Download your audio file for safekeeping\n• Retry the upload when connection improves\n\nNote: Click "Download & Retry" to save your file, or "Cancel" to keep trying.`,
          {
            confirmText: 'Download & Retry',
            cancelText: 'Just Retry',
            thirdButtonText: 'Cancel'
          }
        );

        if (retry === true) {
          // User chose to download and retry
          downloadAudioFile();
          // Reset state for retry
          setError(null);
          setIsLoading(false);
        } else if (retry === false) {
          // User chose to just retry
          setError(null);
          setIsLoading(false);
          // Retry automatically after a short delay
          setTimeout(() => {
            handleTranscribe();
          }, 1000);
        } else {
          // User cancelled
          setError('Upload cancelled. You can try again when ready.');
        }
      } else {
        // Other error - show normal error message
        setError(err.message);
      }

      // Play error notification
      await notifyTranscriptionComplete(false, err.message);
    }
    }, [audioFile, isAuthenticated, hasActiveSubscription, checkTranscriptionLimit, promptUpgrade, isLoading, hasTranscript, transcriptData, transcriptName, user, navigate, showSuccess, showConfirm]);

  // Bulk transcription function
  const handleBulkTranscribe = useCallback(async (audioFiles) => {
	  console.log('[handleBulkTranscribe] Starting bulk transcription for', audioFiles.length, 'files');
	  if (!audioFiles || audioFiles.length === 0) {
		  setError('No audio files provided');
		  return;
	  }
	  if (audioFiles.length > 3) {
		  setError('Maximum 3 files allowed per bulk upload');
		  return;
	  }
	  setIsLoading(true);
	  setError(null);
	  try {
		  const formData = new FormData();
		  // Add all files to formData
		  audioFiles.forEach((file, index) => {
			  formData.append('audio', file);
		  });
		  // Get auth token
		  const token = localStorage.getItem('authToken');
		  const headers = {};
		  if (token) {
			  headers['Authorization'] = `Bearer ${token}`;
		  }
		  console.log(`[handleBulkTranscribe] Sending ${audioFiles.length} files to /transcribe-bulk...`);
		  const response = await fetch('/transcribe-bulk', {
			  method: 'POST',
			  headers,
			  body: formData,
		  });

		  if (!response.ok) {
			  const errData = await response.json();
			  throw new Error(errData.error || `HTTP error! status: ${response.status}`);
		  }
		  const data = await response.json();
		  console.log('Bulk transcription results:', data);

		  // Show success message with results
		  const successCount = data.summary.successful;
		  const totalCount = data.summary.total;


		  if (successCount === totalCount) {
			  showSuccess('Bulk Processing Complete!', `All ${successCount} files were processed successfully!`);
		  } else {
			  showSuccess('Bulk Processing Complete',
				  `Processed ${totalCount} files:\n• ${successCount} successful\n• ${data.summary.failed} failed\n\nCheck console for detailed results.`);
		  }
	  } catch (error) {
		  console.error('Bulk transcription error:', error);
		  setError(error.message);
		  showError('Bulk Processing Failed', error.message);
	  } finally {
		  setIsLoading(false);
	  }
  }, [setIsLoading, setError, showSuccess, showError]);
  // Auto-transcribe when audio is uploaded (desktop and mobile)
  // Works for both authenticated users and guests (Try Rec-Chord)
  useEffect(() => {
    // Only auto-transcribe if we have audio, no transcript, not loading, and haven't triggered yet
    if (hasAudio && !hasTranscript && !isLoading && !error && audioFile && !autoTranscribeTriggered.current) {
      debugLog('[App] Auto-triggering transcription for newly uploaded audio');
      autoTranscribeTriggered.current = true;
      handleTranscribe();
    }
  }, [hasAudio, hasTranscript, isLoading, error, audioFile]); // Removed handleTranscribe and isAuthenticated from deps

  // Simple clipboard copy with user feedback
  const copyToClipboard = useCallback(async (shareUrl, audioText, expiresAt = null) => {
    // Calculate expiry message
    let expiryMessage = 'Link expires in 30 days.';
    if (expiresAt) {
      const expiryDate = new Date(expiresAt);
      const now = new Date();
      const diffMs = expiryDate - now;
      const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
      const diffHours = Math.round(diffMs / (1000 * 60 * 60));

      if (diffDays > 1) {
        expiryMessage = `Link expires in ${diffDays} days.`;
      } else if (diffHours > 1) {
        expiryMessage = `Link expires in ${diffHours} hours.`;
      } else {
        expiryMessage = 'Link expires soon.';
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl);
      showSuccess('Link Copied!', `Share link ${audioText} copied to clipboard!\n\n${expiryMessage}`);
    } catch (copyError) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = shareUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      showSuccess('Link Copied!', `Share link ${audioText} copied to clipboard!\n\n${expiryMessage}`);
    }
  }, [showSuccess]);

  // Cleanup object URL to prevent memory leaks
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const handleTokenClick = useCallback((timestamp) => {
    debugLog('[DEBUG] Token clicked, seeking to time:', timestamp);
    debugLog('[DEBUG] Audio ref exists:', !!audioRef.current);
    if (audioRef.current) {
      debugLog('[DEBUG] Setting audio currentTime from', audioRef.current.currentTime, 'to', timestamp);
      audioRef.current.currentTime = timestamp;
      // Always play audio when clicking on a word, even if paused
      if (audioRef.current.paused) {
        debugLog('[DEBUG] Audio was paused, starting playback');
        audioRef.current.play().catch(err => {
          console.error('[DEBUG] Failed to play audio:', err);
        });
      }
    }
    setCurrentTime(timestamp);
  }, []);

  // Debug current time updates
  useEffect(() => {
    debugLog('[DEBUG] App currentTime updated:', currentTime);
  }, [currentTime]);

  const handleTranscriptChange = useCallback((newData) => {
    // Use history-aware setState for user edits
    setTranscriptDataWithHistory(newData);
  }, [setTranscriptDataWithHistory]);

  // Set up keyboard shortcuts for undo/redo
  useUndoRedoKeyboard(undo, redo, canUndo, canRedo);

  const handleHighThresholdChange = useCallback((value) => {
    const newValue = Math.max(mediumThreshold + 0.001, Math.min(1.0, value));
    setHighThreshold(newValue);
  }, [mediumThreshold]);

  const handleMediumThresholdChange = useCallback((value) => {
    const newValue = Math.max(0.0, Math.min(highThreshold - 0.001, value));
    setMediumThreshold(newValue);
  }, [highThreshold]);

  const handleTypingModeToggle = useCallback(() => {
    setTypingMode(prev => {
      if (!prev) { // entering typing mode
        if (audioRef.current) {
          audioRef.current.pause();
        }
        // Turn off other modes when entering typing mode
        setClickToDeleteMode(false);
        setSuggestionsMode(false);
      }
      return !prev;
    });
  }, []);

  const handleAutoAdvanceToggle = useCallback(() => {
    setAutoAdvance(prev => !prev);
  }, []);

  // Helper function to save transcript settings to server
  const saveTranscriptSettings = useCallback(async (settingsToSave) => {
    if (!currentTranscriptId) return; // Only save if we have a transcript loaded

    try {
      const token = localStorage.getItem('authToken');
      await fetch(`/api/transcripts/${currentTranscriptId}/settings`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ settings: settingsToSave })
      });
    } catch (error) {
      console.error('Failed to save transcript settings:', error);
    }
  }, [currentTranscriptId]);

  const handleShowConfidenceToggle = useCallback(() => {
    setShowConfidence(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ showConfidence: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleShowSegmentsToggle = useCallback(() => {
    setShowSegments(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ showSegments: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleShowConfidenceLegendToggle = useCallback(() => {
    setShowConfidenceLegend(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ showConfidenceLegend: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleEnableBinNavigationToggle = useCallback(() => {
    setEnableBinNavigation(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ enableBinNavigation: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleShowClickToDeleteToggleChange = useCallback(() => {
    setShowClickToDeleteToggle(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ showClickToDeleteToggle: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleShowSuggestionsModeToggleChange = useCallback(() => {
    setShowSuggestionsModeToggle(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ showSuggestionsModeToggle: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleEnableDiarizationToggle = useCallback(() => {
    setEnableDiarization(prev => {
      const newValue = !prev;
      // Save to server if we have a transcript loaded
      saveTranscriptSettings({ enableDiarization: newValue });
      return newValue;
    });
  }, [saveTranscriptSettings]);

  const handleSegmentDurationChange = useCallback((newDuration) => {
    setSegmentDuration(newDuration);
    // Save to server if we have a transcript loaded
    saveTranscriptSettings({ segmentDuration: newDuration });
  }, [saveTranscriptSettings]);

  const deleteCurrentWord = useCallback((segmentIndex, wordIndex) => {
    // This is a user action, so use history-aware setState
    setTranscriptDataWithHistory(prevData => {
      if (!prevData) return null;
      const newData = JSON.parse(JSON.stringify(prevData));
      const segment = newData.segments[segmentIndex];
      if (segment && segment.words) {
        segment.words.splice(wordIndex, 1);
      }
      return newData;
    });
  }, [setTranscriptDataWithHistory]);

  const formatTranscriptForDownload = (data) => {
    if (!data) return null;
  
    const exportData = JSON.parse(JSON.stringify(data)); // Deep copy
  
    for (const segment of exportData.segments) {
      if (!segment.words) continue;
  
      const newWords = [];
      for (const word of segment.words) {
        const newWord = {
          word: word.word,
          start: word.start,
          end: word.end,
          confidence: word.confidence,
        };
  
        if (word.isGhosted || word.deleted) {
          newWord.status = 'deleted';
        } else if (word.isInserted) {
          newWord.status = 'inserted';
        } else {
          newWord.status = 'original';
        }
        
        newWords.push(newWord);
      }
      segment.words = newWords;
    }
  
    return exportData;
  };

  const handleDownload = useCallback(async () => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    const formattedData = formatTranscriptForDownload(transcriptData);
    if (!formattedData) return;

    const jsonString = JSON.stringify(formattedData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);

    // Use transcript name for filename, sanitize for filesystem
    const sanitizedName = (transcriptName || 'transcript').replace(/[^a-z0-9_\-]/gi, '_');

    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizedName}.json`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Clean up after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    // Log download event and save corrected transcript
    try {
      await fetch('/api/log-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'json', segments: formattedData.segments?.length || 0 })
      });
      
      // Save corrected transcript if we have the original hash
      if (originalHash) {
        const saveResponse = await fetch('/api/save-corrected-transcript', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            hash: originalHash, 
            correctedTranscript: formattedData 
          })
        });
        
        if (saveResponse.ok) {
          const saveResult = await saveResponse.json();
          console.log(`Corrected transcript saved: ${saveResult.message}`);
        }
      }
    } catch (error) {
      console.warn('Failed to log download or save corrected transcript:', error);
    }
  }, [transcriptData, transcriptName, isAuthenticated, navigate]);

  const handleDownloadTxt = useCallback(async () => {
    if (!isAuthenticated) {
      navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    if (!transcriptData) return;

    let textContent = '';
    for (const segment of transcriptData.segments) {
      if (!segment.words) continue;
      const segmentText = segment.words
        .filter(word => !word.isGhosted && !word.deleted)
        .map(word => word.word)
        .join(' ');
      textContent += segmentText + '\n';
    }

    const blob = new Blob([textContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);

    // Use transcript name for filename, sanitize for filesystem
    const sanitizedName = (transcriptName || 'transcript').replace(/[^a-z0-9_\-]/gi, '_');

    const a = document.createElement('a');
    a.href = url;
    a.download = `${sanitizedName}.txt`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();

    // Clean up after a short delay to ensure download starts
    setTimeout(() => {
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 100);
    
    // Log download event and save corrected transcript
    try {
      await fetch('/api/log-download', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ format: 'txt', segments: transcriptData.segments?.length || 0 })
      });
      
      // Save corrected transcript if we have the original hash
      if (originalHash) {
        const formattedData = formatTranscriptForDownload(transcriptData);
        if (formattedData) {
          const saveResponse = await fetch('/api/save-corrected-transcript', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ 
              hash: originalHash, 
              correctedTranscript: formattedData 
            })
          });
          
          if (saveResponse.ok) {
            const saveResult = await saveResponse.json();
            console.log(`Corrected transcript saved: ${saveResult.message}`);
          }
        }
      }
    } catch (error) {
      console.warn('Failed to log download or save corrected transcript:', error);
    }
  }, [transcriptData, originalHash, transcriptName, isAuthenticated, navigate]);

  const handleCopy = useCallback(async (textContent) => {
    // Save version when copying (only for authenticated users)
    if (isAuthenticated && currentTranscriptId && transcriptData) {
      try {
        await saveVersion(currentTranscriptId, {
          data: transcriptData,
          reason: 'Copied to clipboard',
          autoSave: false
        });
        console.log('Version saved after copy');
      } catch (versionError) {
        console.warn('Failed to save version after copy:', versionError);
      }
    }

    // Show success notification
    showSuccess('Copied!', 'Transcript copied to clipboard');
  }, [currentTranscriptId, transcriptData, saveVersion, isAuthenticated, showSuccess]);

  const createAndCopyShareLink = useCallback(async (includeAudio, expirationDays = 30) => {
    console.log('[Share] Starting share process:', { currentTranscriptId, hasTranscriptData: !!transcriptData, includeAudio });

    if (!currentTranscriptId) {
      showError('Share Error', 'Please save your transcript first before sharing.');
      return;
    }

    try {
      // Save the current version before sharing
      if (currentTranscriptId && transcriptData) {
        try {
          await saveVersion(currentTranscriptId, {
            data: transcriptData,
            reason: `Shared ${includeAudio ? 'with' : 'without'} audio`,
            autoSave: false
          });
          console.log('Version saved before sharing');
        } catch (versionError) {
          console.warn('Failed to save version before sharing:', versionError);
          // Continue with sharing even if version save fails
        }
      }

      console.log('[Share] Making API request to create share link');
      const authToken = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      const response = await fetch(`/api/transcripts/${currentTranscriptId}/share`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          permissions: 'view',
          expiresIn: `${expirationDays}d`,
          includeAudio: includeAudio
        })
      });

      console.log('[Share] API response status:', response.status);

      if (response.ok) {
        const { shareUrl, expiresAt } = await response.json();
        console.log('[Share] Share link created:', shareUrl);
        const audioText = includeAudio ? 'with audio' : 'without audio';

        // Add UTM parameters for Google Analytics tracking
        const urlWithTracking = new URL(shareUrl);
        urlWithTracking.searchParams.append('utm_source', 'share');
        urlWithTracking.searchParams.append('utm_medium', 'link');
        urlWithTracking.searchParams.append('utm_campaign', 'transcript_share');
        urlWithTracking.searchParams.append('utm_content', includeAudio ? 'with_audio' : 'text_only');
        const trackedUrl = urlWithTracking.toString();

        console.log('[Share] Tracked URL:', trackedUrl);

        // Show the share link in a dialog with copy button
        const expiryDate = new Date(expiresAt);
        const now = new Date();
        const diffMs = expiryDate - now;
        const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.round(diffMs / (1000 * 60 * 60));

        let expiryMessage = `Link expires in ${expirationDays} days`;
        if (diffDays > 1) {
          expiryMessage = `Link expires in ${diffDays} days`;
        } else if (diffHours > 1) {
          expiryMessage = `Link expires in ${diffHours} hours`;
        } else {
          expiryMessage = 'Link expires soon';
        }

        console.log('[Share] Calling showShareLink with URL:', trackedUrl);

        // Extract transcript text for share card
        const transcriptText = transcriptData?.words
          ? transcriptData.words
              .filter(w => !w.deleted)
              .map(w => w.word)
              .join(' ')
          : '';

        // Store share data for visual card
        setShareCardData({
          shareUrl: trackedUrl,
          transcriptText,
          transcriptName,
          duration,
          audioUrl: includeAudio ? audioUrl : null
        });

        await showShareLink('Share Link Created', `${audioText.charAt(0).toUpperCase() + audioText.slice(1)}\n${expiryMessage}`, trackedUrl, () => {
          // Callback to show visual card
          setShowShareCard(true);
        });
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showError('Share Error', `Failed to create share link: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Share Error:', err);
      showError('Share Error', `Error creating share link: ${err.message}`);
    }
  }, [currentTranscriptId, transcriptData, saveVersion, showShareLink, showError]);

  const handleGenerateSummary = useCallback(async () => {
    if (!currentTranscriptId) {
      showError('Summary Error', 'Please save your transcript first before generating a summary.');
      return;
    }

    showAlert('Generating Summary', 'Please wait while AI generates a summary of your transcript...');

    try {
      const response = await fetch(`/api/transcripts/${currentTranscriptId}/generate-summary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ summaryType: 'detailed' })
      });

      if (response.ok) {
        const { summary } = await response.json();
        showSuccess('Summary Generated', summary);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        showError('Summary Error', `Failed to generate summary: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Summary Error:', err);
      showError('Summary Error', `Error generating summary: ${err.message}`);
    }
  }, [currentTranscriptId, showAlert, showSuccess, showError]);

  const handleGenerateName = useCallback(async () => {
    if (!currentTranscriptId) {
      showError('Name Generation Error', 'Please save your transcript first before generating a name.');
      return;
    }

    // Set loading state to show spinner in UI
    const previousName = transcriptName;
    setTranscriptName('Generating name...');

    try {
      const response = await fetch(`/api/transcripts/${currentTranscriptId}/generate-name`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const { name } = await response.json();
        setTranscriptName(name);
        showSuccess('Name Generated', `Your transcript has been renamed to: "${name}"`);
      } else {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        setTranscriptName(previousName); // Restore previous name on error
        showError('Name Generation Error', `Failed to generate name: ${errorData.error}`);
      }
    } catch (err) {
      console.error('Name Generation Error:', err);
      setTranscriptName(previousName); // Restore previous name on error
      showError('Name Generation Error', `Error generating name: ${err.message}`);
    }
  }, [currentTranscriptId, transcriptName, showSuccess, showError, setTranscriptName]);

  const handleShareTranscript = useCallback(async () => {
    console.log('[handleShareTranscript] Called with currentTranscriptId:', currentTranscriptId);

    if (!currentTranscriptId) {
      console.log('[handleShareTranscript] No transcript ID, showing error');
      showError('Share Error', 'Please save your transcript first before sharing.');
      return;
    }

    // Get user's preferred expiration days from localStorage
    const preferences = JSON.parse(localStorage.getItem('userPreferences') || '{}');
    const expirationDays = preferences.shareLinkExpirationDays || 30;

    // Calculate guest expiration message
    let expirationMessage;
    if (!isAuthenticated || isGuestTranscript) {
      // For guests, show time remaining
      if (guestMinutesRemaining !== null && guestMinutesRemaining > 0) {
        if (guestMinutesRemaining >= 60) {
          const hours = Math.floor(guestMinutesRemaining / 60);
          const mins = guestMinutesRemaining % 60;
          expirationMessage = mins > 0 ? `${hours}h ${mins}m` : `${hours} hour${hours > 1 ? 's' : ''}`;
        } else {
          expirationMessage = `${guestMinutesRemaining} minute${guestMinutesRemaining !== 1 ? 's' : ''}`;
        }
        expirationMessage = `⚠️ Link expires in ${expirationMessage}\n\nCreate an account to save transcripts permanently`;
      } else {
        expirationMessage = `⚠️ Link expires in 2 hours\n\nCreate an account to save transcripts permanently`;
      }
    } else {
      expirationMessage = `⚠️ Link valid ${expirationDays} days - anyone can access`;
    }

    console.log('[handleShareTranscript] Showing confirmation dialog');
    // Combined privacy warning and audio options
    const audioChoice = await showConfirm(
      'Share Transcript',
      expirationMessage,
      {
        confirmText: 'With Audio',
        cancelText: 'Text Only',
        thirdButtonText: 'Cancel'
      }
    );

    console.log('[handleShareTranscript] User choice:', audioChoice);

    // audioChoice: true = With Audio, false = Text Only, null = Cancel/X/Escape
    if (audioChoice === null) {
      return; // User canceled
    }

    await createAndCopyShareLink(audioChoice, expirationDays);
  }, [currentTranscriptId, showError, showConfirm, createAndCopyShareLink, isAuthenticated, isGuestTranscript, guestMinutesRemaining]);

  const handleRetryTranscription = useCallback(async () => {
    if (!currentTranscriptId) {
      showError('Retry Error', 'No transcript available to retry.');
      return;
    }

    const confirmed = await showConfirm(
      'Retry Transcription',
      'This will re-transcribe the audio and replace the current transcript. Continue?',
      { confirmText: 'Retry', cancelText: 'Cancel' }
    );

    if (!confirmed) return;

    try {
      setIsLoading(true);
      setError(null);

      const token = localStorage.getItem('authToken');

      const response = await fetch(`/api/transcripts/${currentTranscriptId}/retry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to start retry transcription');
      }

      const result = await response.json();
      const jobId = result.jobId;

      // Start polling for job status
      const pollInterval = setInterval(async () => {
        try {
          const statusResponse = await fetch(`/api/jobs/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });

          if (!statusResponse.ok) {
            clearInterval(pollInterval);
            setIsLoading(false);
            throw new Error('Failed to check job status');
          }

          const jobStatus = await statusResponse.json();

          if (jobStatus.status === 'completed') {
            clearInterval(pollInterval);
            setIsLoading(false);

            // Reload the transcript
            await loadTranscriptById(currentTranscriptId);
          } else if (jobStatus.status === 'failed') {
            clearInterval(pollInterval);
            setIsLoading(false);
            throw new Error(jobStatus.error || 'Transcription failed');
          }
        } catch (error) {
          clearInterval(pollInterval);
          setIsLoading(false);
          throw error;
        }
      }, 2000);

    } catch (err) {
      console.error('Retry Error:', err);
      showError('Retry Error', `Failed to retry transcription: ${err.message}`);
      setIsLoading(false);
    }
  }, [currentTranscriptId, showError, showConfirm, showSuccess, loadTranscriptById]);

  const canShowVisualization = hasTranscript || (hasAudio && (isLoading || currentTranscriptId)); // Show transcript section when we have a transcript, or when audio is loading/processing

  // Debug logging for visualization state
  useEffect(() => {
    console.log('[App] Visualization state:', {
      canShowVisualization,
      hasTranscript,
      hasAudio,
      isLoading,
      currentTranscriptId,
      transcriptDataSegments: transcriptData?.segments?.length || 0
    });
  }, [canShowVisualization, hasTranscript, hasAudio, isLoading, currentTranscriptId, transcriptData]);

  // Debug AudioPlayer props
  useEffect(() => {
    if (canShowVisualization) {
      debugLog('[DEBUG] Passing to AudioPlayer:', { audioUrl, audioFile: audioFile?.name });
    }
  }, [canShowVisualization, audioUrl, audioFile]);



  return (
    <div className={`App ${canShowVisualization ? 'has-transcript' : ''}`}>
      <header className="App-header">
        <div className="header-nav">
          <div className="logo-section">
            <Link to="/" className="logo">Rec-Chord</Link>
            <p className="app-subtitle">Your Voice, Perfectly Penned</p>
          </div>
          <UserNav onHelpToggle={() => setIsHelpVisible(!isHelpVisible)} />
        </div>
      </header>

      {/* Transcript Name - at page level below navbar, but moved into mobile controls on mobile */}
      {canShowVisualization && !isMobile && (
        <div className="page-transcript-name">
          <h1 className="page-transcript-name-text" onClick={() => setIsEditingTranscriptName(true)}>
            {transcriptName === 'Generating name...' ? (
              <>
                <span className="loading-spinner-inline">⏳</span> {transcriptName}
              </>
            ) : (
              transcriptName || 'Untitled Transcript'
            )}
          </h1>
        </div>
      )}

      <main className="App-main">
        {/* Loading indicator when fetching transcript */}
        {searchParams.get('transcript') && !hasAudio && !hasTranscript && !error && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '60px 20px',
            color: '#666'
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '24px', marginBottom: '12px' }}>Loading transcript...</div>
            </div>
          </div>
        )}

        {!hasAudio && !error && !isLoading && !searchParams.get('transcript') && (
          <div className="upload-section">
            <AudioUploader
              onFileUpload={handleFileUpload}
              onTranscribe={handleTranscribe}
	      onBulkTranscribe={handleBulkTranscribe}

              isLoading={isLoading}
              isAudioLoading={isAudioLoading}
              hasAudio={hasAudio}
              hasTranscript={hasTranscript}
              isAuthenticated={isAuthenticated}
              showJsonUpload={process.env.REACT_APP_SHOW_JSON_UPLOAD === 'true'}
              showRecorder={true}
              onDownload={handleDownload}
              onDownloadTxt={handleDownloadTxt}
              showConfirm={showConfirm}
              showAlert={showAlert}
              onLiveTranscriptUpdate={handleLiveTranscriptUpdate}
              onLiveSessionComplete={handleLiveSessionComplete}
              enableBulkUpload={true}
              autoRecord={searchParams.get('record') === 'true'}
            />
          </div>
        )}


        {error && !hasTranscript && (
          <div className="error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Show error message if transcript exists but has no audio */}
        {!hasAudio && hasTranscript && error && !isLoading && (
          <div className="audio-section error-message">
            <p>{error}</p>
          </div>
        )}

        {/* Guest transcript warning banner */}
        {!guestWarningDismissed && (isGuestTranscript || (!isAuthenticated && hasTranscript)) && hasAudio && (
          <div className="guest-warning-banner" style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '12px'
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '600', marginBottom: '8px', color: '#856404' }}>
                Guest Transcript
              </div>
              <div style={{ color: '#856404', fontSize: '14px', lineHeight: '1.5' }}>
                <div>Anyone with this link can view this transcript and listen to the audio.</div>
                {!isAuthenticated && (
                  <div style={{ marginTop: '4px' }}>Deleted in {guestMinutesRemaining !== null ?
                    guestMinutesRemaining + ' minutes' :
                    '2 hours'} unless you create an account.</div>
                )}
              </div>
              <div style={{ marginTop: '12px' }}>
                {isAuthenticated ? (
                  <button
                    onClick={async () => {
                      try {
                        const claimed = await claimGuestTranscript(currentTranscriptId);
                        // Navigate to the new transcript
                        navigate(`/?transcript=${claimed.id}`);
                        setIsGuestTranscript(false);
                        setGuestWarningDismissed(true);
                      } catch (err) {
                        console.error('Failed to claim transcript:', err);
                        setError('Failed to save transcript to your account');
                      }
                    }}
                    style={{
                      backgroundColor: '#28a745',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Save to My Account
                  </button>
                ) : (
                  <button
                    onClick={() => navigate('/auth?redirect=' + encodeURIComponent(window.location.pathname + window.location.search))}
                    style={{
                      backgroundColor: '#007bff',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '14px',
                      fontWeight: '500'
                    }}
                  >
                    Create Account to Save
                  </button>
                )}
              </div>
            </div>
            <button
              onClick={() => setGuestWarningDismissed(true)}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                color: '#856404',
                fontSize: '18px',
                lineHeight: 1
              }}
              aria-label="Dismiss warning"
            >
              ×
            </button>
          </div>
        )}

        {/* Show audio player immediately when audio is available */}
        {hasAudio && (
          isMobile ? (
            // Hidden audio element for mobile
            <audio
              key={audioUrl}
              ref={audioRef}
              src={audioUrl}
              preload="metadata"
              onTimeUpdate={(e) => setCurrentTime(e.target.currentTime)}
              onLoadedMetadata={(e) => {
                setIsAudioLoading(false);
                setDuration(e.target.duration);
                console.log('[Mobile Audio] Metadata loaded:', {
                  duration: e.target.duration,
                  src: e.target.src,
                  readyState: e.target.readyState
                });
              }}
              onCanPlay={() => {
                setIsAudioLoading(false);
                console.log('[Mobile Audio] Can play - audio ready');
              }}
              onLoadStart={() => {
                console.log('[Mobile Audio] Load started');
              }}
              onLoadedData={() => {
                console.log('[Mobile Audio] Data loaded');
              }}
              onWaiting={() => setIsAudioLoading(true)}
              onError={(e) => {
                console.error('[Mobile Audio] Error event:', e);
                console.error('[Mobile Audio] Error details:', {
                  error: e.target.error,
                  errorCode: e.target.error?.code,
                  errorMessage: e.target.error?.message,
                  networkState: e.target.networkState,
                  readyState: e.target.readyState,
                  src: e.target.src,
                  audioUrl: audioUrl
                });
                setIsAudioLoading(false);
              }}
              style={{ display: 'none' }}
            />
          ) : (
            // Full AudioPlayer for desktop
            <div className="audio-section">
              <AudioPlayer
                ref={audioRef}
                audioUrl={audioUrl}
                audioFile={audioFile}
                currentTime={currentTime}
                skipDuration={skipDuration}
                onTimeUpdate={(time) => {
                  debugLog('[DEBUG] AudioPlayer onTimeUpdate called:', time);
                  setCurrentTime(time);
                }}
                onLoadingChange={setIsAudioLoading}
              />
            </div>
          )
        )}

        {/* Mobile controls grouped together - only show on mobile when we have audio */}
        {hasAudio && isMobile && (
          <div className="mobile-controls-wrapper" ref={mobileControlsRef}>
            <div className="mobile-control-buttons">
              {/* Transcript Name on mobile - at top left of control buttons */}
              {canShowVisualization && (
                <div className="mobile-transcript-name-header" onClick={() => setIsEditingTranscriptName(true)}>
                  {transcriptName === 'Generating name...' ? (
                    <>
                      <span className="loading-spinner-inline">⏳</span> {transcriptName}
                    </>
                  ) : (
                    transcriptName || 'Untitled Transcript'
                  )}
                </div>
              )}
              {/* Show transcription status when loading */}
              {!canShowVisualization && isLoading && (
                <div className="mobile-transcription-status">
                  <span className="transcription-spinner">⏳</span>
                  <span>Transcribing audio...</span>
                </div>
              )}
              <MobileTranscriptControlsSettings
                onCopy={handleCopy}
                onShare={handleShareTranscript}
                onSettingsToggle={() => setIsSettingsVisible(!isSettingsVisible)}
                onHelpToggle={() => setIsHelpVisible(!isHelpVisible)}
                showControls={hasTranscript}
              />
            </div>

            <MobileAudioControls
              audioRef={audioRef}
              isPlaying={audioRef?.current && !audioRef.current.paused}
              currentTime={currentTime}
              duration={duration}
              skipDuration={skipDuration}
              onPlay={() => audioRef?.current?.play()}
              onPause={() => audioRef?.current?.pause()}
              onSeek={(time) => {
                if (audioRef?.current) {
                  audioRef.current.currentTime = time;
                  setCurrentTime(time);
                }
              }}
              showControls={hasAudio && isMobile}
              isLoading={isAudioLoading}
              rightButtons={canShowVisualization && (
                <MobileTranscriptControls
                  typingMode={typingMode}
                  onTypingModeToggle={handleTypingModeToggle}
                  clickToDeleteMode={clickToDeleteMode}
                  onClickToDeleteModeToggle={() => {
                    setClickToDeleteMode(prev => {
                      debugLog('[App] Toggling click-to-delete from', prev, 'to', !prev);
                      if (!prev) {
                        // Turn off other modes when entering click-to-delete mode
                        setTypingMode(false);
                        setSuggestionsMode(false);
                      }
                      return !prev;
                    });
                  }}
                  suggestionsMode={suggestionsMode}
                  onSuggestionsModeToggle={() => {
                    setSuggestionsMode(prev => {
                      if (!prev) {
                        // Turn off other modes when entering suggestions mode
                        setTypingMode(false);
                        setClickToDeleteMode(false);
                      }
                      return !prev;
                    });
                  }}
                  onHelpToggle={() => setIsHelpVisible(!isHelpVisible)}
                  showControls={true}
                  showClickToDeleteToggle={showClickToDeleteToggle}
                />
              )}
            />
          </div>
        )}

        {canShowVisualization && (
          <div className="transcript-section">
            <Transcript
              transcriptId={currentTranscriptId}
              data={transcriptData}
              audioRef={audioRef}
              onTokenClick={handleTokenClick}
              onTranscriptChange={handleTranscriptChange}
              deleteCurrentWord={deleteCurrentWord}
              onDownload={handleDownload}
              onDownloadTxt={handleDownloadTxt}
              onCopy={handleCopy}
              onShare={handleShareTranscript}
              onGenerateSummary={handleGenerateSummary}
              onGenerateName={currentTranscriptId ? handleGenerateName : null}
              onRetryTranscription={originalHash ? handleRetryTranscription : null}
              isTranscribing={isLoading}
              currentTime={currentTime}
              highThreshold={highThreshold}
              mediumThreshold={mediumThreshold}
              onHighThresholdChange={handleHighThresholdChange}
              onMediumThresholdChange={handleMediumThresholdChange}
              typingMode={typingMode}
              onTypingModeToggle={handleTypingModeToggle}
              autoAdvance={autoAdvance}
              onAutoAdvanceToggle={handleAutoAdvanceToggle}
              showConfidence={showConfidence}
              onShowConfidenceToggle={handleShowConfidenceToggle}
              showSegments={showSegments}
              onShowSegmentsToggle={handleShowSegmentsToggle}
              segmentDuration={segmentDuration}
              onSegmentDurationChange={handleSegmentDurationChange}
              clickToDeleteMode={clickToDeleteMode}
              onClickToDeleteModeToggle={() => {
                setClickToDeleteMode(prev => {
                  debugLog('[App] Toggling click-to-delete from', prev, 'to', !prev);
                  if (!prev) {
                    // Turn off other modes when entering click-to-delete mode
                    setTypingMode(false);
                    setSuggestionsMode(false);
                  }
                  return !prev;
                });
              }}
              suggestionsMode={suggestionsMode}
              onSuggestionsModeToggle={() => {
                setSuggestionsMode(prev => {
                  if (!prev) {
                    // Turn off other modes when entering suggestions mode
                    setTypingMode(false);
                    setClickToDeleteMode(false);
                  }
                  return !prev;
                });
              }}
              transcriptName={transcriptName}
              onTranscriptNameChange={setTranscriptName}
              isSettingsVisible={isSettingsVisible}
              setIsSettingsVisible={setIsSettingsVisible}
              isHelpVisible={isHelpVisible}
              setIsHelpVisible={setIsHelpVisible}
              showConfidenceLegend={showConfidenceLegend}
              onShowConfidenceLegendToggle={handleShowConfidenceLegendToggle}
              enableBinNavigation={enableBinNavigation}
              onEnableBinNavigationToggle={handleEnableBinNavigationToggle}
              showSuggestionsModeToggle={showSuggestionsModeToggle}
              onShowSuggestionsModeToggleChange={handleShowSuggestionsModeToggleChange}
              showClickToDeleteToggle={showClickToDeleteToggle}
              onShowClickToDeleteToggleChange={handleShowClickToDeleteToggleChange}
              enableDiarization={enableDiarization}
              onEnableDiarizationToggle={handleEnableDiarizationToggle}
            />
          </div>
        )}
      </main>

      <CookieConsent />
      <CustomAlert
        isOpen={alert?.isOpen}
        onClose={alert?.onClose}
        onConfirm={alert?.onConfirm}
        onCancel={alert?.onCancel}
        title={alert?.title}
        message={alert?.message}
        type={alert?.type}
        confirmText={alert?.confirmText}
        cancelText={alert?.cancelText}
        thirdButtonText={alert?.thirdButtonText}
        showCancel={alert?.showCancel}
        shareUrl={alert?.shareUrl}
        onCreateVisualCard={alert?.onCreateVisualCard}
      />

      {/* Shareable Transcript Card */}
      {showShareCard && shareCardData && (
        <ShareableTranscriptCard
          transcriptText={shareCardData.transcriptText}
          transcriptName={shareCardData.transcriptName}
          duration={shareCardData.duration}
          shareUrl={shareCardData.shareUrl}
          audioUrl={shareCardData.audioUrl}
          theme={shareCardTheme}
          onClose={() => setShowShareCard(false)}
          onCopyLink={() => console.log('Link copied from share card')}
        />
      )}

      {/* Transcript Name Edit Dialog */}
      <TranscriptNameEditDialog
        isOpen={isEditingTranscriptName}
        currentName={transcriptName}
        onSave={async (newName) => {
          setTranscriptName(newName);
          setIsEditingTranscriptName(false);

          // Save to database if we have a transcript ID
          if (currentTranscriptId) {
            try {
              const token = localStorage.getItem('authToken');
              const response = await fetch(`/api/transcripts/${currentTranscriptId}`, {
                method: 'PUT',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify({ name: newName })
              });

              if (!response.ok) {
                console.error('[Transcript Name] Failed to save name to database');
              } else {
                console.log('[Transcript Name] Successfully saved name to database:', newName);
              }
            } catch (error) {
              console.error('[Transcript Name] Error saving name:', error);
            }
          }
        }}
        onClose={() => setIsEditingTranscriptName(false)}
      />
    </div>
  );
}

// Redirect /transcript/:id to /?transcript=:id
function TranscriptRedirect() {
  const { transcriptId } = useParams();
  return <Navigate to={`/?transcript=${transcriptId}`} replace />;
}

function App() {
  const routes = (
    <Layout>
      <Routes>
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/callback" element={<Auth />} />
        <Route path="/plans-and-payment" element={<PaymentSetup />} />
        <Route path="/payment-setup" element={<PaymentSetup />} /> {/* Legacy redirect */}
        <Route path="/billing" element={<Billing />} />
        <Route path="/dashboard" element={<Dashboard />} />
        //<Route path="/bulk-upload" element={<BulkUpload />} />
        <Route path="/instructions" element={<Instructions />} />
        <Route path="/about" element={<About />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/speakers" element={<Speakers />} />
        <Route path="/device" element={<DeviceAuth />} />
        <Route path="/transcripts/:transcriptId/versions" element={<TranscriptHistory />} />
        <Route path="/transcript/:transcriptId" element={<TranscriptRedirect />} />
        <Route path="/shared/:shareId" element={<SharedTranscript />} />
        <Route path="/try" element={<TryRecChord />} />
        <Route path="/" element={<MainApp />} />
        <Route path="*" element={<Navigate to="/" replace />} />
	<Route path="/contact" element={<ContactUs />} />

      </Routes>
    </Layout>
  );

  return (
    <Router>
      <PageViewTracker />
      <ConnectivityHandler />
      <ThemeProvider>
        <AuthProvider>
          {stripePromise ? <Elements stripe={stripePromise}>{routes}</Elements> : routes}
        </AuthProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
